import { atom, useRecoilState } from "recoil";
import { useCallback } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import { listOrnaments as listOrnamentsAPI, getOwnerships as getOwnershipsAPI, RoomOrnament } from "@/Data/World";

type OrnamentState = {
    ornaments: Record<string, RoomOrnament>;
    wallpapers: Record<string, RoomOrnament>;
    ownedIds: string[];
    loading: boolean;
    error: string | null;
};

const STORAGE_KEY = 'world:ornament';

const loadFromStorage = (): Partial<OrnamentState> => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) { return JSON.parse(stored); }
    } catch (e) {
        console.error('Failed to load Ornament from storage:', e);
    }
    return {};
};

const saveToStorage = (state: OrnamentState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({
            ornaments: state.ornaments,
            wallpapers: state.wallpapers,
            ownedIds: state.ownedIds,
        }));
    } catch (e) {
        console.error('Failed to save Ornament to storage:', e);
    }
};

const defaultState: OrnamentState = {
    ornaments: {},
    wallpapers: {},
    ownedIds: [],
    loading: false,
    error: null,
};

const ornamentState = atom<OrnamentState>({
    key: 'ornamentState',
    default: { ...defaultState, ...loadFromStorage() },
});

/**
 * Hook for managing ornament catalog
 */
export const useOrnament = () => {
    const [state, setState] = useRecoilState(ornamentState);

    const updateState = useCallback((newState: Partial<OrnamentState>) => {
        setState(prev => {
            const updated = { ...prev, ...newState };
            saveToStorage(updated);
            return updated;
        });
    }, [setState]);

    const { ornaments, wallpapers, ownedIds, loading, error } = state;

    /**
     * Fetch all ornaments and wallpapers from API
     */
    const fetchOrnaments = useCallback(async (): Promise<void> => {
        updateState({ loading: true, error: null });

        try {
            const [ornamentsRes, wallpapersRes] = await Promise.all([
                listOrnamentsAPI('ornament'),
                listOrnamentsAPI('wallpaper'),
            ]);

            const ornamentsMap: Record<string, RoomOrnament> = {};
            ornamentsRes.forEach(o => { ornamentsMap[o.ornamentid] = o; });

            const wallpapersMap: Record<string, RoomOrnament> = {};
            wallpapersRes.forEach(w => { wallpapersMap[w.ornamentid] = w; });

            setState(prev => {
                const updated = {
                    ...prev,
                    ornaments: ornamentsMap,
                    wallpapers: wallpapersMap,
                    loading: false,
                };
                saveToStorage(updated);
                return updated;
            });
        } catch (e) {
            console.error('Failed to fetch ornaments:', e);
            updateState({ loading: false, error: 'Failed to fetch ornaments' });
        }
    }, [setState, updateState]);

    /**
     * Fetch user's owned ornament IDs
     */
    const fetchOwnerships = useCallback(async (): Promise<string[]> => {
        try {
            const ids = await getOwnershipsAPI();

            setState(prev => {
                const updated = { ...prev, ownedIds: ids };
                saveToStorage(updated);
                return updated;
            });

            return ids;
        } catch (e) {
            console.error('Failed to fetch ownerships:', e);
            return [];
        }
    }, [setState]);

    /**
     * Get ornament by ID
     */
    const getOrnament = useCallback((ornamentid: string): RoomOrnament | null => {
        return ornaments[ornamentid] || wallpapers[ornamentid] || null;
    }, [ornaments, wallpapers]);

    /**
     * Check if catalog is loaded
     */
    const isLoaded = Object.keys(ornaments).length > 0 || Object.keys(wallpapers).length > 0;

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        updateState({ error: null });
    }, [updateState]);

    return {
        // State
        ornaments,
        wallpapers,
        ownedIds,
        loading,
        isLoaded,
        error,

        // Actions
        fetchOrnaments,
        fetchOwnerships,
        getOrnament,
        clearError,
    };
};
